/**
 * atomberg-customer-images.js
 *
 * Drives the two [data-atomberg-customer-images] roots rendered by
 * sections/atomberg-customer-images.liquid: fetches admin-curated customer
 * photos from the Atomberg backend (GET {api_base_url}/api/public/customer-images)
 * and renders color filter chips, a 1:1 photo grid and cursor pagination.
 *
 *   - data-context="inline"  — the grid below Trust Badges. Fetches eagerly on
 *     load; "View more images" grows the section in place, and once the
 *     section is scrolled fully out of view it collapses back to page one so
 *     it doesn't keep taking up the page.
 *   - data-context="overlay" — the grid inside the atomberg-sheet dialog
 *     (bottom sheet on mobile, centered dialog on desktop) opened by the
 *     gallery button. Lazy: only fetches the first time it's opened.
 *
 * Images/colors/counts intentionally do NOT live in a Shopify metafield —
 * expected volume is ~200-300 images per color. So nothing is known at page
 * render time; the inline instance's first response also decides whether to
 * hide the whole section and the gallery button (unless the theme editor is
 * open). See docs/components/atomberg-customer-images/README.md.
 *
 * The "View real images" button is rendered by the section, then moved into
 * the gallery's dots row at runtime (Liquid can't nest into Horizon's
 * _product-media-gallery block) — same outside-in layering as
 * atomberg-pdp-zoom.js.
 *
 * Clicking a photo in the INLINE grid opens a full-screen zoom dialog
 * (prev/next + thumbnail strip) — never inside the overlay dialog, since a
 * zoom dialog stacked on the "View real images" dialog would look like a
 * dialog over a dialog.
 *
 * Fires 'atomberg:customer-images-event' ({name, payload}) for the button
 * click and every color filter change — atomberg-moengage-datalayer.js and
 * atomberg-gtm-datalayer.js both listen for it, same generic-pipe shape as
 * atomberg:warranty-event.
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);
  if (w._atombergCustomerImagesInit) return;
  w._atombergCustomerImagesInit = true;

  var DESKTOP_QUERY = '(min-width: 750px)';
  var ALL = 'All';

  /** @type {Record<string, string>} color name → variant image URL, for the filter chips */
  var swatches = {};

  /** @type {{utm_source: string|null, utm_campaign: string|null}} */
  var utmParams = (function () {
    var params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source'),
      utm_campaign: params.get('utm_campaign'),
    };
  })();

  /**
   * @typedef {object} PublicImage
   * @property {string} id
   * @property {string} url
   * @property {string} alt
   * @property {string} [color]
   *
   * @typedef {object} CustomerImagesResponse
   * @property {PublicImage[]} images
   * @property {string|null} next_cursor
   * @property {boolean} has_more
   * @property {Record<string, number>} counts
   * @property {string[]} color_priority
   */

  /**
   * @param {string} name
   * @param {Record<string, any>} payload
   */
  function fireCustomerImagesEvent(name, payload) {
    document.dispatchEvent(
      new CustomEvent('atomberg:customer-images-event', {
        detail: { name: name, payload: payload },
        bubbles: true,
      })
    );
  }

  /**
   * Whole rows only: rows × columns at the current breakpoint.
   * @param {HTMLElement} root
   * @returns {number}
   */
  function pageSize(root) {
    var breakpoint = window.matchMedia(DESKTOP_QUERY).matches ? 'desktop' : 'mobile';
    var rows = Number(root.getAttribute('data-rows-' + breakpoint));
    var columns = Number(root.getAttribute('data-columns-' + breakpoint));
    return rows * columns;
  }

  /**
   * @param {HTMLElement} root
   * @param {string} color
   * @param {string|null} cursor
   * @returns {Promise<CustomerImagesResponse|null>}
   */
  function fetchPage(root, color, cursor) {
    var apiBaseUrl = root.getAttribute('data-api-base-url') || '';
    var shop = root.getAttribute('data-shop-domain') || '';
    var productId = root.getAttribute('data-product-id') || '';

    if (!apiBaseUrl || !shop || !productId) return Promise.resolve(null);

    var params = new URLSearchParams();
    params.set('shop', shop);
    params.set('product_id', productId);
    params.set('color', color);
    params.set('limit', String(pageSize(root)));
    if (cursor) params.set('cursor', cursor);
    if (utmParams.utm_source) params.set('utm_source', utmParams.utm_source);
    if (utmParams.utm_campaign) params.set('utm_campaign', utmParams.utm_campaign);

    return fetch(apiBaseUrl + '/api/public/customer-images?' + params.toString(), {
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .catch(function () {
        return null;
      });
  }

  /** @param {HTMLElement} root */
  function CustomerImagesInstance(root) {
    /** @type {HTMLElement} */
    this.root = root;
    /** @type {HTMLElement} */
    this.filtersEl = /** @type {HTMLElement} */ (root.querySelector('[data-atomberg-ci-filters]'));
    /** @type {HTMLElement} */
    this.photosEl = /** @type {HTMLElement} */ (root.querySelector('[data-atomberg-ci-photos]'));
    /** @type {HTMLButtonElement} */
    this.viewMoreEl = /** @type {HTMLButtonElement} */ (
      root.querySelector('[data-atomberg-ci-view-more]')
    );
    /** @type {HTMLTemplateElement} */
    this.itemTemplate = /** @type {HTMLTemplateElement} */ (
      root.querySelector('[data-atomberg-ci-item-template]')
    );
    /** @type {string} */
    this.activeColor = ALL;
    /** @type {string|null} */
    this.cursor = null;
    /** @type {Promise<CustomerImagesResponse|null>|null} */
    this.loading = null;
    /** @type {number} */
    this.requestId = 0;
    /** @type {HTMLElement|null} the dialog's scrolling body; null for the inline grid, which never moves the page */
    this.scroller = /** @type {HTMLElement|null} */ (root.closest('.atomberg-sheet__body'));
    /** @type {{count: number, cursor: string|null, hasMore: boolean}} first page of the active color — what collapse() returns to */
    this.pageOne = { count: 0, cursor: null, hasMore: false };
    /** @type {boolean} true once "View more" has appended beyond page one */
    this.expanded = false;
    /** @type {PublicImage[]} every image currently rendered, in display order — drives the zoom dialog */
    this.images = [];
    /** @type {Record<string, number>} last response's counts, for analytics payloads */
    this.counts = {};
    /** @type {number} how many times "View more images" has been clicked here, for Customer_Images_Load_More_Click's load_number */
    this.loadMoreCount = 0;

    var self = this;
    this.viewMoreEl.addEventListener('click', function () {
      var imagesBefore = self.images.length;
      self.loadMoreCount++;
      self.fetchImages(true).then(function (data) {
        if (!data) return;
        fireCustomerImagesEvent('Customer_Images_Load_More_Click', {
          product_id: self.root.getAttribute('data-product-id') || '',
          product_name: self.root.getAttribute('data-product-name') || '',
          context: self.root.getAttribute('data-context') || '',
          color: self.activeColor,
          images_before: imagesBefore,
          images_after: self.images.length,
          total_available: self.counts[self.activeColor] || 0,
          has_more: data.has_more,
          load_number: self.loadMoreCount,
        });
      });
    });
  }

  /**
   * @param {boolean} append
   * @returns {Promise<CustomerImagesResponse|null>}
   */
  CustomerImagesInstance.prototype.fetchImages = function (append) {
    var self = this;
    // Rapid chip switches can resolve out of order — only the latest request may render.
    var requestId = ++this.requestId;
    return fetchPage(this.root, this.activeColor, append ? this.cursor : null).then(
      function (data) {
        if (!data || requestId !== self.requestId) return null;
        self.renderImages(data.images, append);
        self.cursor = data.next_cursor;
        self.viewMoreEl.hidden = !data.has_more;
        if (append) {
          self.expanded = true;
        } else {
          self.pageOne = {
            count: data.images.length,
            cursor: data.next_cursor,
            hasMore: data.has_more,
          };
          self.expanded = false;
        }
        return data;
      }
    );
  };

  /** @returns {Promise<CustomerImagesResponse|null>} */
  CustomerImagesInstance.prototype.load = function () {
    if (this.loading) return this.loading;
    var self = this;
    this.loading = this.fetchImages(false).then(function (data) {
      if (!data) {
        // Let the next open retry instead of caching a failed request.
        self.loading = null;
        return null;
      }
      self.counts = data.counts;
      self.renderFilters(data.color_priority, data.counts);
      // The grid root renders `hidden` (nothing is known at Liquid render time);
      // without this the dialog opens with an empty body.
      self.root.hidden = false;
      return data;
    });
    return this.loading;
  };

  /**
   * Opens on `color` — loads first if this is the first open.
   * @param {string} color
   */
  CustomerImagesInstance.prototype.show = function (color) {
    // The sheet stays in the DOM while closed, so it would otherwise reopen at the last scroll position.
    if (this.scroller) this.scroller.scrollTop = 0;
    if (!this.loading) {
      this.activeColor = color;
      this.load();
      return;
    }
    this.selectColor(color);
  };

  /**
   * Page size depends on the breakpoint's column count, so after crossing it
   * the loaded page would no longer be whole rows — start over from page one.
   */
  CustomerImagesInstance.prototype.refresh = function () {
    if (!this.loading) return;
    this.cursor = null;
    this.fetchImages(false);
  };

  /** Drops every appended page and restores page one — no refetch, page one is still in the DOM. */
  CustomerImagesInstance.prototype.collapse = function () {
    if (!this.expanded) return;
    // A "View more" still in flight must not append after the collapse.
    this.requestId++;
    while (this.photosEl.children.length > this.pageOne.count) {
      this.photosEl.removeChild(/** @type {Element} */ (this.photosEl.lastElementChild));
    }
    // Keep in lockstep with photosEl — the zoom dialog indexes into this by DOM position.
    this.images.length = this.pageOne.count;
    this.cursor = this.pageOne.cursor;
    this.viewMoreEl.hidden = !this.pageOne.hasMore;
    this.expanded = false;
  };

  /** @param {string} color */
  CustomerImagesInstance.prototype.selectColor = function (color) {
    if (color === this.activeColor) return;
    var previousColor = this.activeColor;
    this.activeColor = color;
    this.cursor = null;
    if (this.scroller) this.scroller.scrollTop = 0;
    this.syncFilterState();
    this.fetchImages(false);
    fireCustomerImagesEvent('Customer_Images_Color_Filter_Changed', {
      product_id: this.root.getAttribute('data-product-id') || '',
      product_name: this.root.getAttribute('data-product-name') || '',
      previous_color: previousColor,
      selected_color: color,
      image_count: this.counts[color] || 0,
    });
  };

  CustomerImagesInstance.prototype.syncFilterState = function () {
    var chips = this.filtersEl.querySelectorAll('[data-atomberg-ci-color]');
    for (var i = 0; i < chips.length; i++) {
      var isActive = chips[i].getAttribute('data-atomberg-ci-color') === this.activeColor;
      chips[i].classList.toggle('is-active', isActive);
      chips[i].setAttribute('aria-selected', String(isActive));
    }
  };

  /**
   * @param {string[]} colorPriority
   * @param {Record<string, number>} counts
   */
  CustomerImagesInstance.prototype.renderFilters = function (colorPriority, counts) {
    var self = this;
    var colors = [ALL].concat(colorPriority);
    this.filtersEl.innerHTML = '';

    colors.forEach(function (color) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'atomberg-customer-images-grid__filter';
      chip.setAttribute('role', 'tab');
      chip.setAttribute('data-atomberg-ci-color', color);

      var swatchHex = color === ALL ? '' : swatches[color];
      if (swatchHex) {
        var swatch = document.createElement('span');
        swatch.className = 'atomberg-customer-images-grid__filter-swatch';
        swatch.style.backgroundColor = swatchHex;
        chip.classList.add('atomberg-customer-images-grid__filter--swatch');
        chip.appendChild(swatch);
      }

      var text = document.createElement('span');
      text.className = 'atomberg-customer-images-grid__filter-text';
      var label = document.createElement('span');
      label.className = 'atomberg-customer-images-grid__filter-label';
      label.textContent = color;
      var count = document.createElement('span');
      count.className = 'atomberg-customer-images-grid__filter-count';
      count.textContent = '(' + (counts[color] || 0) + ')';
      text.appendChild(label);
      text.appendChild(count);
      chip.appendChild(text);

      chip.addEventListener('click', function () {
        self.selectColor(color);
      });
      self.filtersEl.appendChild(chip);
    });

    this.syncFilterState();
  };

  /**
   * @param {PublicImage[]} images
   * @param {boolean} append
   */
  CustomerImagesInstance.prototype.renderImages = function (images, append) {
    if (!append) {
      this.photosEl.innerHTML = '';
      this.images = [];
    }
    this.images = this.images.concat(images);
    // Under a color chip every photo is that color, so the caption only adds information in "All".
    var showCaption = this.activeColor === ALL;
    var content = this.itemTemplate.content;
    for (var i = 0; i < images.length; i++) {
      var node = /** @type {DocumentFragment} */ (content.cloneNode(true));
      var img = /** @type {HTMLImageElement} */ (
        node.querySelector('.atomberg-customer-images-grid__img')
      );
      var captionEl = /** @type {HTMLElement} */ (
        node.querySelector('.atomberg-customer-images-grid__caption')
      );
      var caption = showCaption ? images[i].color || '' : '';
      img.src = images[i].url;
      img.alt = images[i].alt;
      captionEl.textContent = caption;
      captionEl.hidden = !caption;
      this.photosEl.appendChild(node);
    }
  };

  /**
   * Puts the button in the gallery's dots row (slideshow-controls[controls-on-media],
   * the strip right under the image) so dots sit left and the button right.
   * Single-media products render no dots row — there it overlays the image corner.
   * @param {HTMLElement} ribbon
   */
  function placeRibbon(ribbon) {
    var gallery = document.querySelector('media-gallery');
    if (!gallery || gallery.contains(ribbon)) return;
    var dotsRow = gallery.querySelector('slideshow-controls[controls-on-media]');
    var target = dotsRow || gallery.querySelector('slideshow-container') || gallery;
    ribbon.classList.toggle('atomberg-customer-images-ribbon--overlay', !dotsRow);
    target.appendChild(ribbon);
  }

  /** @param {HTMLElement} ribbon */
  function keepRibbonInGallery(ribbon) {
    placeRibbon(ribbon);
    // media-gallery.js replaceWith()s the whole <media-gallery> on every
    // variant change, taking the button with the old one — put it back.
    new MutationObserver(function () {
      placeRibbon(ribbon);
    }).observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Collapses the inline grid back to page one once it's scrolled fully out of view.
   * @param {CustomerImagesInstance} instance
   */
  function collapseWhenOutOfView(instance) {
    var scroller = /** @type {HTMLElement} */ (
      document.scrollingElement || document.documentElement
    );
    new IntersectionObserver(function (entries) {
      var entry = entries[0];
      if (entry.isIntersecting || !instance.expanded) return;

      if (entry.boundingClientRect.bottom > 0) {
        instance.collapse();
        return;
      }

      // Above the viewport, shrinking pulls the content being read upward — compensate by hand (Safari has no scroll anchoring), anchoring off so Chrome/Firefox don't double-correct.
      var previousAnchor = scroller.style.overflowAnchor;
      scroller.style.overflowAnchor = 'none';
      var bottomBefore = instance.root.getBoundingClientRect().bottom;
      instance.collapse();
      // 'instant' overrides base.css's `html { scroll-behavior: smooth }` — an animated correction is itself the visible shift.
      window.scrollBy({
        top: instance.root.getBoundingClientRect().bottom - bottomBefore,
        behavior: 'instant',
      });
      // Restore on the next frame, after the browser has laid out the scroll change.
      requestAnimationFrame(function () {
        scroller.style.overflowAnchor = previousAnchor;
      });
    }).observe(instance.root);
  }

  /** Thumbnails within this many places of the end trigger loading the next page. */
  var ZOOM_LOAD_MORE_THRESHOLD = 3;

  /**
   * Full-screen zoom dialog for the inline grid only. Reads straight from the
   * given CustomerImagesInstance's own `images`/`cursor` rather than taking a
   * snapshot, so nearing the end of what's loaded can trigger that same
   * instance's fetchImages(true) — the grid behind the dialog ends up with
   * the same images appended, exactly as if "View more" had been clicked. A
   * native <dialog> rather than atomberg-sheet: .showModal() promotes it to
   * the browser's top layer, so it stacks correctly regardless of any
   * transformed ancestor, and ESC/focus-trap come for free.
   * @param {HTMLDialogElement} dialog
   * @param {CustomerImagesInstance} instance
   */
  function ZoomLightbox(dialog, instance) {
    /** @type {HTMLDialogElement} */
    this.dialog = dialog;
    /** @type {CustomerImagesInstance} */
    this.instance = instance;
    /** @type {HTMLImageElement} */
    this.imageEl = /** @type {HTMLImageElement} */ (
      dialog.querySelector('[data-atomberg-ci-zoom-image]')
    );
    /** @type {HTMLElement} */
    this.thumbsEl = /** @type {HTMLElement} */ (
      dialog.querySelector('[data-atomberg-ci-zoom-thumbs]')
    );
    /** @type {HTMLButtonElement} */
    this.prevEl = /** @type {HTMLButtonElement} */ (
      dialog.querySelector('[data-atomberg-ci-zoom-prev]')
    );
    /** @type {HTMLButtonElement} */
    this.nextEl = /** @type {HTMLButtonElement} */ (
      dialog.querySelector('[data-atomberg-ci-zoom-next]')
    );
    /** @type {number} */
    this.index = 0;
    /** @type {boolean} */
    this.loadingMore = false;
    /** @type {number} how many thumbnails are already in thumbsEl, so re-syncing only appends the new ones */
    this.renderedCount = 0;
    /** @type {HTMLElement} kept as thumbsEl's last child; toggled visible while loading more */
    this.loaderEl = document.createElement('div');
    this.loaderEl.className = 'atomberg-customer-images-zoom__thumb-loader';
    this.loaderEl.hidden = true;
    this.thumbsEl.appendChild(this.loaderEl);

    var self = this;
    var closeEl = dialog.querySelector('[data-atomberg-ci-zoom-close]');
    if (closeEl)
      closeEl.addEventListener('click', function () {
        self.close();
      });
    this.prevEl.addEventListener('click', function () {
      self.show(self.index - 1);
    });
    this.nextEl.addEventListener('click', function () {
      self.show(self.index + 1);
    });
    // No click-outside-to-close: the stage/thumbs fill the full-bleed dialog edge to edge
    // (that's the point of a full-screen viewer), so there's no backdrop gap to click —
    // close is the X button, or the native Escape handling <dialog>.showModal() gives for free.
    dialog.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') self.show(self.index - 1);
      else if (e.key === 'ArrowRight') self.show(self.index + 1);
    });
    // showModal() makes the dialog itself fill the screen with no internal overflow, but
    // doesn't touch the page behind it — that page stays scrollable (confirmed: identical
    // scrollHeight/clientHeight on the dialog, but html.scrollHeight far exceeds
    // html.clientHeight), which is the "vertical scroll" a user can trigger while it's
    // open. The native 'close' event covers every way the dialog can close (X button, this
    // file's own close(), and Escape, which bypasses close() entirely) in one place, rather
    // than unlocking at each closing call site. A dedicated attribute, not atomberg-sheet's
    // data-atomberg-sheet-lock, since that one reference-counts against its own open-sheets
    // Set — toggling it here directly could unlock scroll while an unrelated sheet is open.
    dialog.addEventListener('close', function () {
      document.documentElement.removeAttribute('data-atomberg-ci-zoom-open');
    });
  }

  /** @param {number} index */
  ZoomLightbox.prototype.open = function (index) {
    this.renderedCount = 0;
    this.thumbsEl.innerHTML = '';
    this.thumbsEl.appendChild(this.loaderEl);
    this.syncThumbs();
    this.show(index);
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    document.documentElement.setAttribute('data-atomberg-ci-zoom-open', '');
  };

  ZoomLightbox.prototype.close = function () {
    if (typeof this.dialog.close === 'function') this.dialog.close();
  };

  /** @param {number} index */
  ZoomLightbox.prototype.show = function (index) {
    var images = this.instance.images;
    var max = images.length - 1;
    if (max < 0) return;
    this.index = Math.max(0, Math.min(index, max));
    var image = images[this.index];
    this.imageEl.src = image.url;
    this.imageEl.alt = image.alt || '';
    this.prevEl.disabled = this.index === 0;
    this.nextEl.disabled = this.index === max && !this.instance.cursor;
    this.syncActiveThumb();
    this.maybeLoadMore();
  };

  /** Appends thumbnails for any images the instance has loaded since the last sync. */
  ZoomLightbox.prototype.syncThumbs = function () {
    var images = this.instance.images;
    for (; this.renderedCount < images.length; this.renderedCount++) {
      var image = images[this.renderedCount];
      if (!image) break;
      this.thumbsEl.insertBefore(this.makeThumb(image, this.renderedCount), this.loaderEl);
    }
  };

  /**
   * @param {PublicImage} image
   * @param {number} index
   * @returns {HTMLButtonElement}
   */
  ZoomLightbox.prototype.makeThumb = function (image, index) {
    var self = this;
    var thumb = document.createElement('button');
    thumb.type = 'button';
    thumb.className = 'atomberg-customer-images-zoom__thumb';
    var img = document.createElement('img');
    img.src = image.url;
    img.alt = '';
    img.loading = 'lazy';
    img.width = 64;
    img.height = 64;
    thumb.appendChild(img);
    thumb.addEventListener('click', function () {
      self.show(index);
    });
    return thumb;
  };

  ZoomLightbox.prototype.syncActiveThumb = function () {
    var thumbs = this.thumbsEl.querySelectorAll('.atomberg-customer-images-zoom__thumb');
    for (var i = 0; i < thumbs.length; i++) {
      thumbs[i].classList.toggle('is-active', i === this.index);
    }
    var active = thumbs[this.index];
    if (active) active.scrollIntoView({ block: 'nearest', inline: 'center' });
  };

  /** Within the last few loaded images, with more to fetch and nothing already in flight. */
  ZoomLightbox.prototype.maybeLoadMore = function () {
    var self = this;
    var instance = this.instance;
    if (this.loadingMore || !instance.cursor) return;
    if (this.index < instance.images.length - ZOOM_LOAD_MORE_THRESHOLD) return;
    this.loadingMore = true;
    this.loaderEl.hidden = false;
    instance.fetchImages(true).then(function () {
      self.loadingMore = false;
      self.loaderEl.hidden = true;
      self.syncThumbs();
    });
  };

  function init() {
    var inlineRoot = /** @type {HTMLElement|null} */ (
      document.querySelector('[data-atomberg-customer-images][data-context="inline"]')
    );
    if (!inlineRoot) return;

    var swatchesEl = document.querySelector('[data-atomberg-ci-swatches]');
    if (swatchesEl) swatches = JSON.parse(swatchesEl.textContent || '{}');

    var overlayRoot = /** @type {HTMLElement|null} */ (
      document.querySelector('[data-atomberg-customer-images][data-context="overlay"]')
    );
    var sectionEl = /** @type {HTMLElement|null} */ (
      inlineRoot.closest('.atomberg-customer-images')
    );
    var ribbon = /** @type {HTMLElement|null} */ (
      document.querySelector('[data-atomberg-ci-ribbon]')
    );
    var isDesignMode = !!sectionEl && sectionEl.getAttribute('data-design-mode') === 'true';
    var inline = new CustomerImagesInstance(inlineRoot);
    var instances = [inline];

    if (overlayRoot) {
      var overlay = new CustomerImagesInstance(overlayRoot);
      instances.push(overlay);
      var ribbonTrigger = ribbon && ribbon.querySelector('[data-atomberg-sheet-trigger]');
      if (ribbonTrigger) {
        ribbonTrigger.addEventListener('click', function () {
          overlay.show(ALL);
          fireCustomerImagesEvent('Customer_Images_View_Click', {
            product_id: inline.root.getAttribute('data-product-id') || '',
            product_name: inline.root.getAttribute('data-product-name') || '',
            image_count: inline.counts[ALL] || 0,
          });
        });
      }
    }

    if (ribbon) keepRibbonInGallery(ribbon);
    collapseWhenOutOfView(inline);

    // Zoom is inline-grid only (see file header) — the click listener never goes on the overlay root.
    var zoomDialog = /** @type {HTMLDialogElement|null} */ (
      document.querySelector('[data-atomberg-ci-zoom]')
    );
    if (zoomDialog) {
      var zoom = new ZoomLightbox(zoomDialog, inline);
      inline.photosEl.addEventListener('click', function (e) {
        var item = /** @type {HTMLElement} */ (e.target).closest(
          '.atomberg-customer-images-grid__item'
        );
        if (!item) return;
        var items = Array.prototype.slice.call(inline.photosEl.children);
        zoom.open(items.indexOf(item));
      });
    }

    window.matchMedia(DESKTOP_QUERY).addEventListener('change', function () {
      instances.forEach(function (instance) {
        instance.refresh();
      });
    });

    inline.load().then(function (data) {
      var hasImages = !!data && (data.counts[ALL] || 0) > 0;
      if (hasImages || isDesignMode) {
        if (ribbon) ribbon.hidden = false;
        return;
      }
      if (sectionEl) sectionEl.hidden = true;
      if (ribbon) ribbon.hidden = true;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
